import * as THREE from 'three';
import { FROST, METEOR, onRoad, quantize, skillTarget, type PlayerState, type Sim, type SimEvent } from '@orb/sim';
import { L, SKILLS, type SkillId } from './config';
import type { Horde } from './Horde';
import type { Driver } from './Driver';
import type { Fx } from '../fx/Fx';
import { Projectiles } from '../fx/Projectiles';
import { fxUniforms } from '../fx/common';
import { Shape } from '../fx/Particles';
import { BarrierView } from '../fx/BarrierView';

interface Meteor { player: number; at: THREE.Vector3; from: THREE.Vector3; pos: THREE.Vector3; prev: THREE.Vector3; time: number; body: number; trail?: THREE.Vector3 }
const _p = new THREE.Vector3(), _v = new THREE.Vector3(), _q = new THREE.Vector3(), _c = new THREE.Color();
export { FROST };

/**
 * Player skills as seen and aimed: targeting is local UI, the cast goes to the simulation,
 * and everything shown (meteors, the wall, the burn, the frost) follows its events and state.
 * All barriers share one reusable view; meteors share twelve GPU slots.
 */
export class Abilities {
  targeting: SkillId | null = null;
  readonly preview = new THREE.Vector3(0.9, 0.06, L.roadZ);
  private readonly meteors: Meteor[] = [];
  /** The sim refused this client's last cast (shown by the skill bar for a moment). */
  denied: { skill: SkillId; reason: 'cooldown' | 'target' | 'not_equipped'; time: number } | null = null;
  private readonly bodies = new Projectiles(12);
  private readonly wall: BarrierView;
  private readonly reticle: THREE.Mesh;
  private readonly burnGround: THREE.Mesh;
  private readonly burnMat: THREE.ShaderMaterial;
  private burnFx = 0; private trailT = 0;
  private barrierFx = 0; private barrierHit = 0; private wallHit = 0;
  private readonly sim: Sim;

  constructor(private readonly driver: Driver, private readonly horde: Horde, private readonly fx: Fx, scene: THREE.Scene) {
    this.sim = driver.sim;
    scene.add(this.bodies.mesh);
    this.wall = new BarrierView(scene);
    // Targeting previews drawn on the ground, one look per skill: a red threat circle with the meteors'
    // falling shadows, a creeping frost disc, ghost crystals across the road for the wall.
    this.reticle = new THREE.Mesh(new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2), new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uMode: { value: 0 }, uAspect: { value: 1 } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv * 2.0 - 1.0; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform float uTime, uMode, uAspect; varying vec2 vUv;
        float h2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h2(i), h2(i + vec2(1, 0)), f.x), mix(h2(i + vec2(0, 1)), h2(i + vec2(1, 1)), f.x), f.y); }
        float ring(float d, float r, float w) { return 1.0 - smoothstep(w, w + fwidth(d) * 1.5, abs(d - r)); }
        void main() {
          float d = length(vUv), a = atan(vUv.y, vUv.x);
          vec3 col = vec3(0.0);
          if (uMode < 0.5) {
            if (d > 1.02) discard;
            // meteor: a threat circle with a rotating dashed rim and six pulsing impact shadows
            float dash = step(0.5, fract(a / 6.2832 * 24.0 + uTime * 0.4));
            float rim = ring(d, 0.97, 0.018) * (0.55 + 0.45 * dash) + ring(d, 0.9, 0.006) * 0.5;
            float fill = (1.0 - smoothstep(0.2, 1.0, d)) * 0.07 * (0.8 + 0.2 * sin(uTime * 6.0));
            float marks = 0.0;
            for (int k = 0; k < 6; k++) {
              float ang = float(k) * 1.047 + 0.3, rr = 0.25 + 0.4 * fract(float(k) * 0.618);
              vec2 c = vec2(cos(ang), sin(ang)) * rr;
              float pulse = fract(uTime * 1.2 + float(k) * 0.17);
              marks += ring(length(vUv - c), 0.06 + pulse * 0.12, 0.012) * (1.0 - pulse) + (1.0 - smoothstep(0.0, 0.07, length(vUv - c))) * 0.35;
            }
            col = vec3(1.0, 0.16, 0.05) * (rim * 0.85 + fill * 1.6 + marks * 0.55);
          } else if (uMode < 1.5) {
            // ice wall: a translucent strip across the road with ghost crystal outlines rising along it
            vec2 p = vec2(vUv.x, vUv.y);
            if (abs(p.x) > 1.0 || abs(p.y) > 1.0) discard;
            float edge = (1.0 - smoothstep(0.82, 0.98, abs(p.x))) ;
            float cells = fract((p.y * 0.5 + 0.5) * 7.0), id = floor((p.y * 0.5 + 0.5) * 7.0);
            float tip = 1.0 - abs(cells - 0.5) * 2.0;
            float crystal = smoothstep(0.03, 0.0, abs(abs(p.x) - (0.25 + 0.55 * tip * (0.7 + 0.3 * h2(vec2(id))))));
            float shimmer = 0.6 + 0.4 * sin(uTime * 4.0 + p.y * 9.0);
            col = vec3(0.4, 0.8, 1.0) * (edge * 0.12 + crystal * 0.7 * shimmer + ring(abs(p.x), 0.98, 0.02) * 0.9);
          } else {
            // frost wave: a frost disc with a six-fold snowflake etching, a torn rime edge and a ripple
            float rim = 0.94 + (vn(vec2(cos(a), sin(a)) * 3.0 + 2.0) - 0.5) * 0.08;
            if (d > 1.02) discard;
            float flake = smoothstep(0.035, 0.0, abs(sin(a * 3.0)) * d) * (1.0 - smoothstep(0.2, 0.92, d));
            float branches = smoothstep(0.04, 0.0, abs(sin(a * 3.0 + 0.5)) * d) * step(0.35, d) * (1.0 - smoothstep(0.5, 0.8, d)) * 0.6;
            float rime = smoothstep(0.5, 0.95, vn(vUv * 14.0 + uTime * 0.2) * 0.6 + vn(vUv * 31.0) * 0.4) * (1.0 - smoothstep(0.3, 1.0, d)) * 0.12;
            float ripple = ring(d, fract(uTime * 0.7) * 0.95, 0.02) * (1.0 - fract(uTime * 0.7));
            float edge = 1.0 - smoothstep(0.0, 0.03, abs(d - rim));
            col = vec3(0.45, 0.78, 1.15) * (edge * 1.0 + flake * 0.5 + branches * 0.8 + rime + ripple * 0.5 + (1.0 - d) * 0.05);
          }
          gl_FragColor = vec4(col, 1.0);
        }`,
    }));
    this.reticle.name = 'skillTarget'; this.reticle.visible = false; this.reticle.renderOrder = 7; scene.add(this.reticle);
    // Scorched ground: a charred patch with a torn edge darkens the stone (premultiplied alpha), and branching
    // fissures smoulder in it. The embers cool first, then the char fades.
    this.burnMat = new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uStrength: { value: 1 } }, transparent: true, depthWrite: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor, side: THREE.DoubleSide, forceSinglePass: true,
      vertexShader: 'varying vec2 vUv;void main(){vUv=uv*2.-1.;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
      fragmentShader: /* glsl */ `varying vec2 vUv; uniform float uTime, uStrength;
        float h2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h2(i), h2(i + vec2(1, 0)), f.x), mix(h2(i + vec2(0, 1)), h2(i + vec2(1, 1)), f.x), f.y); }
        void main() {
          float d = length(vUv);
          float rim = 0.78 + (vn(vUv * 3.0 + 7.0) - 0.5) * 0.35;
          if (d > 1.0) discard;
          float charMask = 1.0 - smoothstep(rim - 0.25, rim, d);
          // fissures between baked plates: cellular (Voronoi) edges, slightly warped, wider near the centre
          vec2 q = vUv * 4.2 + vec2(vn(vUv * 2.5), vn(vUv * 2.5 + 5.3)) * 0.6;
          vec2 cell = floor(q), fr = fract(q);
          float f1 = 8.0, f2 = 8.0;
          for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
            vec2 o = vec2(float(i), float(j)), pnt = o + vec2(h2(cell + o), h2(cell + o + 17.3)) * 0.85 - fr;
            float dd = dot(pnt, pnt);
            if (dd < f1) { f2 = f1; f1 = dd; } else if (dd < f2) f2 = dd;
          }
          float gap = sqrt(f2) - sqrt(f1);
          float fissure = smoothstep(0.09 + (1.0 - d) * 0.05, 0.0, gap) * charMask;
          // small hot embers glinting in the char
          float embers = smoothstep(0.82, 0.95, vn(vUv * 24.0 + uTime * 0.4)) * charMask;
          float flicker = 0.75 + 0.25 * sin(uTime * 7.0 + vUv.x * 9.0) * sin(uTime * 5.3 - vUv.y * 7.0);
          float heat = smoothstep(0.0, 0.6, uStrength);
          vec3 glow = (vec3(1.5, 0.3, 0.04) * fissure + vec3(1.6, 0.6, 0.12) * embers) * flicker * heat * 1.3;
          float soot = charMask * 0.72 * min(1.0, uStrength * 1.6);
          gl_FragColor = vec4(glow, soot);
        }`,
    });
    this.burnGround = new THREE.Mesh(new THREE.PlaneGeometry(2,2).rotateX(-Math.PI/2),this.burnMat);
    this.burnGround.name = 'skillBurnGround'; this.burnGround.visible = false; this.burnGround.renderOrder = 8; scene.add(this.burnGround);
  }

  private get me(): PlayerState { return this.driver.player; }
  /** Seconds left per skill, for the skill bar. */
  get cooldown(): readonly number[] { return this.me.cooldown; }
  get hasteT() { return this.me.hasteT; }
  /** The one skill this player may cast (the lobby's pick). */
  get active(): SkillId { return this.me.skill; }
  get activeIndex() { return SKILLS.findIndex((s) => s.id === this.me.skill); }

  activate(index = this.activeIndex) {
    const skill = SKILLS[index]; if (!skill || skill.id !== this.active || this.cooldown[index] > 0) return false;
    if (skill.id === 'meteor' || skill.id === 'barrier' || skill.id === 'frost') {
      this.targeting = this.targeting === skill.id ? null : skill.id;
      return true;
    }
    this.targeting = null;
    this.driver.send({ type: 'cast', skill: skill.id, x: 0, z: 0 });
    return true;
  }

  cancel() { this.targeting = null; }
  aim(x: number, z: number) {
    if (!this.targeting) return;
    const at = skillTarget(this.targeting, x, z);
    this.preview.set(at.x, 0.055, at.z);
  }
  confirm(x: number, z: number) {
    const skill = this.targeting;
    // judge the target exactly as the sim will: on the quantized point that goes over the wire
    const qx = quantize(x), qz = quantize(z);
    if (!skill || !onRoad(qx, qz)) return false;
    const index = SKILLS.findIndex((s) => s.id === skill);
    if (this.cooldown[index] > 0) return false;
    this.aim(qx, qz);
    this.driver.send({ type: 'cast', skill, x: qx, z: qz });
    this.targeting = null;
    return true;
  }

  events(events: readonly SimEvent[]) {
    for (const ev of events) {
      if (ev.type === 'skill') this.cast(ev.player, ev.skill, ev.x, ev.z);
      else if (ev.type === 'skill_denied') { if (ev.player === this.driver.me) this.denied = { skill: ev.skill, reason: ev.reason, time: 1.8 }; }
      else if (ev.type === 'meteor_impact') this.meteorImpact(ev.x, ev.z);
      else if (ev.type === 'freeze') { const v = this.horde.get(ev.id); if (v) this.fx.el.freezeCrust(this.horde.aim(v, _p), v.e.scale); }
      else if (ev.type === 'barrier_hit') { this.barrierHit = ev.z; this.wallHit = 1; }
      else if (ev.type === 'barrier_end') this.barrierEnd(ev.broken, ev.x, ev.z);
    }
  }

  private cast(player: number, skill: SkillId, x: number, z: number) {
    // overcharge flares at the caster's own seal; the partner's seal gets its own look in stage 5
    if (skill === 'haste') {
      // the overload flares around the caster's own board: our seal, or the ally's altar across the road
      if (player === this.driver.me) this.fx.ring(_p.set(L.sealX, 0.05, 6), 0xffd66d, 3.3, 0.55, 1.3);
      else this.fx.ring(_p.set(0, 0.55, L.partnerZ), 0xffd66d, 2.6, 0.55, 1.1);
      return;
    }
    if (skill === 'frost') {
      this.fx.el.frostWave(_p.set(x, 0.055, z), FROST.radius);
      this.fx.flash(_p.set(x, 0.6, z), 0x9fd8ff, 7, 4, 0.4);
      this.fx.shake(0.1);
      return;
    }
    if (skill === 'meteor') {
      // a new hail replaces only the same player's rocks still in the air
      for (let k = this.meteors.length - 1; k >= 0; k--) {
        const m = this.meteors[k]; if (m.player !== player) continue;
        if (m.body >= 0) this.bodies.remove(m.body);
        this.meteors.splice(k, 1);
      }
      for (let i = 0; i < METEOR.count; i++) {
        const a = i * 2.39996, r = i ? 0.35 + (i % 3) * 0.27 : 0;
        const at = new THREE.Vector3(x + Math.cos(a) * r, 0.12, z + Math.sin(a) * r * 0.7);
        // a lower, steeper fall keeps the comet tails inside the frame instead of streaking across it
        const from = at.clone().add(_v.set(-1.3, 4.4 + i * 0.12, -0.7));
        const pos = from.clone();
        this.meteors.push({ player, at, from, pos, prev: pos.clone(), time: -i * METEOR.stagger, body: -1 });
        // telegraph: a ring closing on the impact point and a glow that heats up as the rock falls
        const eta = METEOR.fall + i * METEOR.stagger;
        this.fx.p.emit({ x: at.x, y: 0.05, z: at.z, color: _c.set(0xff6a2a), intensity: 1.6, life: eta, size: 1.1, sizeEnd: 0.25, shape: Shape.Ring, fade: 0.25 });
        this.fx.p.emit({ x: at.x, y: 0.045, z: at.z, color: _c.set(0xff4a14), intensity: 1.2, life: eta, size: 0.3, sizeEnd: 1.2, shape: Shape.Floor, fade: 0.15 });
      }
      this.fx.floorFlash(_p.set(x, 0.055, z), 0xff6835, 2.7, 0.9, 0.65);
      return;
    }
    // ice bursts out of the road: a frost line, glittering chips and a breath of cold
    this.wall.raise(x, z);
    this.fx.p.emit({ x, y: 0.035, z, color: _c.set(0x74c3ff), intensity: 1.4, life: 1.6, size: 0.6, sizeEnd: 1.5, shape: Shape.Frost, fade: 1 });
    this.fx.floorFlash(_p.set(x, 0.03, z), 0x7fd6ff, 1.4, 0.4, 1.4);
    this.fx.shards(_p.set(x, 0.2, z), 0xbfe6ff, 16, 1.8, 1.8);
    for (let dz = -1; dz <= 1.01; dz += 0.5) this.fx.el.smoke(x, 0.15, z + dz, 0, 0.2, 0, 0.3, 0.8, 1.1, 0xc4dcef, 0.22, 0.1);
    this.fx.shake(0.06);
  }

  private meteorImpact(x: number, z: number) {
    let best = -1, bd = Infinity;
    this.meteors.forEach((m, i) => { const d = Math.hypot(m.at.x - x, m.at.z - z); if (d < bd) { bd = d; best = i; } });
    const m = best >= 0 && bd < 0.05 ? this.meteors.splice(best, 1)[0] : null;
    if (m && m.body >= 0) this.bodies.remove(m.body);
    this.fx.meteorImpact(_p.set(x, 0.12, z));
  }

  private barrierEnd(broken: boolean, x: number, z: number) {
    this.wall.end(broken);
    if (broken) {
      this.fx.shards(_p.set(x, 0.5, z), 0x9adfff, 26, 2.6, 2.2);
      this.fx.sparks(_p, 0x8fe0ff, 16, 3, { up: 0.6, life: 0.4, intensity: 2.6 });
      this.fx.flash(_p, 0x5fd0ff, 8, 3.5, 0.3);
      this.fx.ring(_p.set(x, 0.04, z), 0x65bed3, 1.8, 0.4, 2);
      this.fx.shake(0.12);
      for (let dz = -1; dz <= 1.01; dz += 0.5) this.fx.el.smoke(x, 0.2, z + dz, 0, 0.3, 0, 0.3, 0.8, 1, 0xc4dcef, 0.2);
    } else {
      // the wall melts away: meltwater pools along its line under rising vapour
      for (let dz = -1; dz <= 1.01; dz += 0.5) {
        this.fx.p.emit({ x: x + (Math.random() - 0.5) * 0.2, y: 0.045, z: z + dz, color: _c.set(0x3f86b8), intensity: 1, life: 2.4, size: 0.35, shape: Shape.Splat, fade: 1, body: 0.55, delay: 0.3 + Math.random() * 0.3 });
        this.fx.el.smoke(x, 0.3, z + dz, 0, 0.35, 0, 0.3, 0.9, 1.3, 0xc4dcef, 0.25, 0.1);
      }
    }
  }

  /** Tick-rate visuals: falling rocks, chips off the wall, smouldering ground. */
  tick() {
    const dt = 1 / 60;
    if (this.denied && (this.denied.time -= dt) <= 0) this.denied = null;
    for (const m of this.meteors) {
      m.prev.copy(m.pos); m.time += dt;
      if (m.time < 0) continue;
      if (m.body < 0) m.body = this.bodies.add('r', 5, m.pos, _v.set(1.8, -6.5, 1), 2);
      const t = Math.min(1, m.time / METEOR.fall); m.pos.lerpVectors(m.from, m.at, t * t);
    }
    const b = this.sim.state.barrier;
    this.barrierFx -= dt;
    if (b && this.barrierHit && this.barrierFx <= 0) {
      // chips fly from where the blow landed; the rune light spits sparks
      this.barrierFx = 0.1;
      _p.set(b.x - 0.22, 0.35 + Math.random() * 0.5, this.barrierHit);
      this.fx.sparks(_p, 0x8fe0ff, 4, 1.6, { intensity: 2, life: 0.22, size: 0.022 });
      this.fx.shards(_p, 0xbfe6ff, 3, 1.1, 1.4);
      this.fx.dust(_p.setY(0.05), 0.12, 1, 0.4);
    }
    this.barrierHit = 0;
    const burn = this.me.burn;
    if (burn && burn.remaining > 0 && (this.burnFx -= dt) <= 0) {
      this.burnFx = 0.14;
      this.fx.burning(_p.set(burn.x, 0.045, burn.z), burn.radius, Math.min(1, burn.remaining));
    }
  }
  render(alpha: number, dt: number, camera: THREE.Camera) {
    this.trailT += dt; const trail = this.trailT >= 1 / 30; if (trail) this.trailT %= 1 / 30;
    for (const m of this.meteors) {
      if (m.time < 0 || m.body < 0) continue;
      _p.lerpVectors(m.prev, m.pos, alpha);
      this.bodies.set(m.body, _p, _v.subVectors(m.at, m.from));
      if (trail) {
        // the rock accelerates; fill the whole segment it covered so the tail stays continuous
        const last = m.trail ?? (m.trail = _p.clone()), dist = last.distanceTo(_p), steps = Math.min(6, Math.max(1, Math.ceil(dist / 0.16)));
        _v.subVectors(m.at, m.from).multiplyScalar(1 / METEOR.fall);
        for (let k = 1; k <= steps; k++) this.fx.el.fireballTrail(_q.lerpVectors(last, _p, k / steps), _v, 1.4, 1.6 / steps);
        last.copy(_p);
        this.fx.embers(_p, 0xffa547, 1, { size: 0.06, life: 0.35, intensity: 1.6 });
      }
    }
    this.bodies.flush();
    const burn = this.me.burn;
    this.burnGround.visible = !!burn && burn.remaining > 0;
    if (this.burnGround.visible && burn) {
      this.burnGround.position.set(burn.x, 0.03, burn.z); this.burnGround.scale.setScalar(burn.radius);
      this.burnMat.uniforms.uStrength.value = Math.min(1, burn.remaining);
    }
    this.reticle.visible = this.targeting !== null;
    if (this.targeting) {
      this.reticle.position.copy(this.preview);
      const t = this.targeting, r = t === 'frost' ? FROST.radius : METEOR.burnRadius;
      this.reticle.scale.set(t === 'barrier' ? 0.3 : r, 1, t === 'barrier' ? 1.18 : r);
      (this.reticle.material as THREE.ShaderMaterial).uniforms.uMode.value = t === 'barrier' ? 1 : t === 'frost' ? 2 : 0;
    }
    const b = this.sim.state.barrier;
    this.wallHit = Math.max(0, this.wallHit - dt * 5);
    this.wall.update(dt, b ? Math.max(0, b.hp / b.maxHp) : 0, b ? this.wallHit : 0, camera);
  }
}
